<template>
  <div class="tw:flex tw:flex-1 tw:flex-col tw:overflow-auto">
    <StreamFiltersAudience
      v-model:audience="audience"
      v-model:desktop-monthly-visits="desktopMonthlyVisits"
      v-model:mobile-monthly-visits="mobileMonthlyVisits"
      class="tw:p-4"
    />
    <template v-if="canShowAdvertisingValue">
      <BaseSeparator />
      <StreamFiltersMinMaxFilter
        v-model="advertisementRate"
        v-model:enabled="advertisementRateEnabled"
        class="tw:p-4"
        data-testid="advertisement-rate"
      >
        <template #heading>
          <div class="tw:flex tw:items-center tw:gap-1">
            {{
              getLocaleText(
                "stream_filters_metrics.advertising_value_equivalency"
              )
            }}

            <QIcon name="info">
              <StreemTooltip
                anchor="top middle"
                self="bottom middle"
              >
                {{
                  getLocaleText(
                    "stream_filters_metrics.advertising_value_equivalency_tooltip"
                  )
                }}
              </StreemTooltip>
            </QIcon>
          </div>
        </template>
      </StreamFiltersMinMaxFilter>
    </template>
    <BaseSeparator />
    <StreamFiltersSentiment
      v-model:sentiment="sentiment"
      v-model:sentiment-enabled="sentimentEnabled"
      v-model:unrated-sentiment="unratedSentiment"
      class="tw:p-4"
    />
    <BaseSeparator />
    <StreamFiltersImpactScore v-model:impact-score="impactScore" />
    <BaseSeparator />
    <div class="tw:p-4">
      <div class="tw:mb-2 tw:font-bold">
        {{ getLocaleText("stream_filters_metrics.react_score") }}
      </div>
      <ReactScoreExpandable
        v-model:aggregate-emotionality="aggregateEmotionality"
        v-model:aggregate-fakeness="aggregateFakeness"
        v-model:aggregate-harmful="aggregateHarmful"
        v-model:aggregate-spam="aggregateSpam"
        v-model:react-score="reactScore"
        v-model:react-score-average-selected="reactScoreAverageSelected"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseSeparator, StreemTooltip } from "shared/components/base";
import ReactScoreExpandable from "shared/components/selectors/expandables/ReactScoreExpandable.vue";
import type { StreamFiltersMinMax } from "shared/helpers/StreamFilters";
import features from "shared/services/features";
import type { Stream } from "shared/types";

import StreamFiltersAudience from "./StreamFiltersAudience.vue";
import StreamFiltersImpactScore from "./StreamFiltersImpactScore.vue";
import StreamFiltersMinMaxFilter from "./StreamFiltersMinMax.vue";
import StreamFiltersSentiment from "./StreamFiltersSentiment.vue";

interface StreamFiltersMediaProps {
  stream: Stream;
}

defineProps<StreamFiltersMediaProps>();

const impactScore = defineModel<StreamFiltersMinMax[]>("impactScore", {
  default: () => [],
});

const advertisementRate = defineModel<StreamFiltersMinMax>(
  "advertisementRate",
  { required: true }
);

const advertisementRateEnabled = ref(
  Object.values(advertisementRate.value).some((range) => range !== null)
);

const audience = defineModel<StreamFiltersMinMax>("audience", {
  required: true,
});

const desktopMonthlyVisits = defineModel<StreamFiltersMinMax>(
  "desktopMonthlyVisits",
  { required: true }
);

const mobileMonthlyVisits = defineModel<StreamFiltersMinMax>(
  "mobileMonthlyVisits",
  { required: true }
);

const sentiment = defineModel<StreamFiltersMinMax[]>("sentiment", {
  default: () => [],
});

const sentimentEnabled = defineModel<boolean>("sentimentEnabled");
const unratedSentiment = defineModel<boolean>("unratedSentiment");

const reactScore = defineModel<StreamFiltersMinMax>("reactScore", {
  required: true,
});

const reactScoreAverageSelected = defineModel<boolean>(
  "reactScoreAverageSelected"
);

const aggregateEmotionality = defineModel<StreamFiltersMinMax>(
  "aggregateEmotionality",
  {
    required: true,
  }
);

const aggregateFakeness = defineModel<StreamFiltersMinMax>(
  "aggregateFakeness",
  {
    required: true,
  }
);

const aggregateSpam = defineModel<StreamFiltersMinMax>("aggregateSpam", {
  required: true,
});

const aggregateHarmful = defineModel<StreamFiltersMinMax>("aggregateHarmful", {
  required: true,
});

const canShowAdvertisingValue = computed(() =>
  features.has("advertising_value_mentions")
);
</script>
